// ======================================================================
// FILE: src/modules/wms/calendar/pages/CalendarFormPage.js
// MODULE: Work Management System (WMS)
// FEATURE: Calendar Management
// PURPOSE:
//   Create / Edit Calendar
//   Workdays Configuration
//   Shift Configuration
//   Holiday Configuration
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import wmsCalendarService
from "../../services/wmsCalendarService";

import CalendarWorkdaysTab
from "../components/CalendarWorkdaysTab";

import CalendarShiftsTab
from "../components/CalendarShiftsTab";

import CalendarHolidaysTab
from "../components/CalendarHolidaysTab";

const DEFAULT_FORM = {

  calendar_code: "",
  calendar_name: "",

  calendar_type: "WORKING",

  timezone: "Asia/Kolkata",

  is_default: false,

  description: "",
  remarks: "",
};

export default function CalendarFormPage() {

  const navigate =
    useNavigate();

  const { id } =
    useParams();

  const isEdit =
    Boolean(id);

  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [activeTab, setActiveTab] =
    useState("details");

  const [calendarId, setCalendarId] =
    useState(id || null);

  const [form, setForm] =
    useState(DEFAULT_FORM);

  // ==========================================================
  // LOAD
  // ==========================================================

  async function loadData() {

    if (!id) {
      return;
    }

    try {

      setLoading(true);

      const data =
        await wmsCalendarService
          .getCalendar(id);

      setForm({

        calendar_code:
          data.calendar_code || "",

        calendar_name:
          data.calendar_name || "",

        calendar_type:
          data.calendar_type || "WORKING",

        timezone:
          data.timezone || "Asia/Kolkata",

        is_default:
          data.is_default || false,

        description:
          data.description || "",

        remarks:
          data.remarks || "",
      });

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.message
        || "Failed to load calendar"
      );

    } finally {

      setLoading(false);
    }
  }

  useEffect(() => {

    loadData();

  }, [id]);

  // ==========================================================
  // SAVE
  // ==========================================================

  async function handleSave() {

    if (!form.calendar_code) {

      alert(
        "Calendar Code is required"
      );

      return;
    }

    if (!form.calendar_name) {

      alert(
        "Calendar Name is required"
      );

      return;
    }

    try {

      setSaving(true);

      let result;

      if (isEdit) {

        result =
          await wmsCalendarService
            .updateCalendar(
              id,
              form
            );

      } else {

        result =
          await wmsCalendarService
            .createCalendar(
              form
            );

        setCalendarId(
          result.id
        );
      }

      alert(
        isEdit
          ? "Calendar updated successfully"
          : "Calendar created successfully"
      );

      if (!isEdit && result?.id) {

        navigate(
          `/wms/calendars/${result.id}/edit`
        );
      }

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.message
        || "Failed to save calendar"
      );

    } finally {

      setSaving(false);
    }
  }

  // ==========================================================
  // UI
  // ==========================================================

  return (

    <div className="container-fluid">

      {/* ==================================================== */}
      {/* HEADER */}
      {/* ==================================================== */}

      <div
        className="d-flex justify-content-between align-items-center mb-4"
      >

        <div>

          <h3>

            {
              isEdit
                ? "Edit Calendar"
                : "Create Calendar"
            }

          </h3>

          <div className="text-muted">

            Calendar Configuration
            Management

          </div>

        </div>

        <div>

          <button
            className="btn btn-secondary me-2"
            onClick={() =>
              navigate("/wms/calendars")
            }
          >
            Back
          </button>

          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >

            {
              saving
                ? "Saving..."
                : "Save Calendar"
            }

          </button>

        </div>

      </div>

      {/* ==================================================== */}
      {/* CALENDAR DETAILS */}
      {/* ==================================================== */}

      <div className="card mb-4">

        <div className="card-header">

          <strong>
            Calendar Details
          </strong>

        </div>

        <div className="card-body">

          {loading ? (

            <div>
              Loading...
            </div>

          ) : (

            <div className="row">

              <div className="col-md-3">

                <label>
                  Calendar Code
                </label>

                <input
                  className="form-control"
                  value={
                    form.calendar_code
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      calendar_code:
                        e.target.value,
                    })
                  }
                />

              </div>

              <div className="col-md-4">

                <label>
                  Calendar Name
                </label>

                <input
                  className="form-control"
                  value={
                    form.calendar_name
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      calendar_name:
                        e.target.value,
                    })
                  }
                />

              </div>

              <div className="col-md-2">

                <label>
                  Calendar Type
                </label>

                <select
                  className="form-control"
                  value={
                    form.calendar_type
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      calendar_type:
                        e.target.value,
                    })
                  }
                >

                  <option value="WORKING">
                    Working
                  </option>

                  <option value="BUSINESS">
                    Business
                  </option>

                  <option value="SHIFT">
                    Shift
                  </option>

                </select>

              </div>

              <div className="col-md-3">

                <label>
                  Timezone
                </label>

                <input
                  className="form-control"
                  value={
                    form.timezone
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      timezone:
                        e.target.value,
                    })
                  }
                />

              </div>

              <div className="col-md-3 mt-3">

                <div className="form-check">

                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={
                      form.is_default
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        is_default:
                          e.target.checked,
                      })
                    }
                  />

                  <label
                    className="form-check-label"
                  >
                    Default Calendar
                  </label>

                </div>

              </div>

              <div className="col-md-12 mt-3">

                <label>
                  Description
                </label>

                <textarea
                  rows="3"
                  className="form-control"
                  value={
                    form.description
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      description:
                        e.target.value,
                    })
                  }
                />

              </div>

              <div className="col-md-12 mt-3">

                <label>
                  Remarks
                </label>

                <textarea
                  rows="2"
                  className="form-control"
                  value={
                    form.remarks
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      remarks:
                        e.target.value,
                    })
                  }
                />

              </div>

            </div>

          )}

        </div>

      </div>

      {/* ==================================================== */}
      {/* TABS */}
      {/* ==================================================== */}

      {(calendarId || isEdit) && (

        <>

          <ul
            className="nav nav-tabs mb-3"
          >

            <li className="nav-item">

              <button
                className={`nav-link ${
                  activeTab === "workdays"
                    ? "active"
                    : ""
                }`}
                onClick={() =>
                  setActiveTab(
                    "workdays"
                  )
                }
              >
                Workdays
              </button>

            </li>

            <li className="nav-item">

              <button
                className={`nav-link ${
                  activeTab === "shifts"
                    ? "active"
                    : ""
                }`}
                onClick={() =>
                  setActiveTab(
                    "shifts"
                  )
                }
              >
                Shifts
              </button>

            </li>

            <li className="nav-item">

              <button
                className={`nav-link ${
                  activeTab === "holidays"
                    ? "active"
                    : ""
                }`}
                onClick={() =>
                  setActiveTab(
                    "holidays"
                  )
                }
              >
                Holidays
              </button>

            </li>

          </ul>

          {/* ================================================= */}
          {/* TAB CONTENT */}
          {/* ================================================= */}

          {activeTab ===
            "workdays" && (

            <CalendarWorkdaysTab
              calendarId={
                calendarId || id
              }
            />

          )}

          {activeTab ===
            "shifts" && (

            <CalendarShiftsTab
              calendarId={
                calendarId || id
              }
            />

          )}

          {activeTab ===
            "holidays" && (

            <CalendarHolidaysTab
              calendarId={
                calendarId || id
              }
            />

          )}

        </>

      )}

    </div>
  );
}